import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, OnDestroy, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  CacheLookupPolicy,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  AccountInfo,
} from '@azure/msal-browser';
import { catchError, debounceTime, exhaustMap, filter, finalize, map, shareReplay, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, Observable, Subject, from, fromEvent, merge, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { API_SCOPES } from './msal.config';
import { AdminApiService } from '../admin-console/admin-api.service';

type AdminClaimDecision = boolean | null;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private router = inject(Router);
  private injector = inject(Injector);
  private document = inject(DOCUMENT);
  private readonly destroying$ = new Subject<void>();
  private sessionExpiredRedirectStarted = false;
  private adminStatusAccountKey: string | null = null;
  private adminStatusRequest$: Observable<boolean> | null = null;

  // Signals for reactive state management
  private _isAuthenticated = signal<boolean>(false);
  private _isLoading = signal<boolean>(false);
  private _account = signal<AccountInfo | null>(null);
  private _backendAdminStatus = signal<boolean | null>(null);
  private _adminStatusLoading = signal<boolean>(false);

  // Public computed signals
  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly isLoading = computed(() => this._isLoading());
  readonly account = computed(() => this._account());
  readonly displayName = computed(() => this._account()?.name ?? 'User');
  readonly email = computed(() => this._account()?.username ?? '');
  readonly adminStatusLoading = computed(() => this._adminStatusLoading());
  readonly isAdmin = computed(() => {
    const account = this._account();
    if (!account) return false;
    const claimDecision = this.getAdminDecisionFromClaims(account);
    if (claimDecision !== null) return claimDecision;
    return this._backendAdminStatus() ?? false;
  });

  constructor() {
    this.initializeMsal();
    this.initializeResumeSessionCheck();
  }

  private initializeMsal(): void {
    // Check initial account state
    this.checkAccount();

    // Subscribe to account changes
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED
        ),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.checkAccount();
      });

    // Subscribe to interaction status
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.checkAccount();
        this._isLoading.set(false);
      });
  }

  private checkAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const currentAccount = this.msalService.instance.getActiveAccount();
      // Only set active account if not already set to avoid triggering ACTIVE_ACCOUNT_CHANGED loop
      if (!currentAccount || currentAccount.homeAccountId !== accounts[0].homeAccountId) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
      this._account.set(accounts[0]);
      this._isAuthenticated.set(true);
      this.updateAdminStatusForAccount(accounts[0]);
    } else {
      this._account.set(null);
      this._isAuthenticated.set(false);
      this.resetAdminStatus();
    }
  }

  /**
   * Validate the session when the user returns to the application. Browsers can
   * suspend timers in background tabs, so visibility and focus events are more
   * reliable than relying on a token-expiry timeout alone.
   */
  private initializeResumeSessionCheck(): void {
    if (environment.playwrightAuthBypass) return;

    const browserWindow = this.document.defaultView;
    if (!browserWindow) return;

    merge(
      fromEvent(browserWindow, 'focus'),
      fromEvent(this.document, 'visibilitychange')
    ).pipe(
      filter(() => this.document.visibilityState === 'visible'),
      debounceTime(100),
      withLatestFrom(this.msalBroadcastService.inProgress$),
      filter(([, status]) => status === InteractionStatus.None),
      exhaustMap(() => this.validateSessionOnResume()),
      takeUntil(this.destroying$)
    ).subscribe();
  }

  private validateSessionOnResume(): Observable<unknown> {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];

    if (!account) return EMPTY;

    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: API_SCOPES,
        account,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      })
    ).pipe(
      catchError((error: unknown) => {
        if (error instanceof InteractionRequiredAuthError) {
          this.redirectToSessionExpiredLogin();
        }

        // A temporary network or service error should not end the resume-event
        // subscription or incorrectly mark the user's session as expired.
        return EMPTY;
      })
    );
  }

  resolveAdminStatus(): Observable<boolean> {
    if (environment.playwrightAuthBypass) {
      return of(true);
    }

    const account = this._account();
    if (!account) {
      return of(false);
    }

    const claimDecision = this.getAdminDecisionFromClaims(account);
    if (claimDecision !== null) {
      return of(claimDecision);
    }

    return this.ensureBackendAdminStatus(account);
  }

  login(redirectStartPage?: string): void {
    this._isLoading.set(true);
    this.msalService.loginRedirect({
      scopes: API_SCOPES,
      ...(redirectStartPage ? { redirectStartPage } : {}),
    });
  }

  logout(): void {
    this._isLoading.set(true);
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      account: account,
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri,
    });
  }

  redirectToSessionExpiredLogin(): void {
    if (this.sessionExpiredRedirectStarted) return;

    this.sessionExpiredRedirectStarted = true;

    void this.router.navigate(['/login'], {
      queryParams: { sessionExpired: 'true' },
    });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private updateAdminStatusForAccount(account: AccountInfo): void {
    if (environment.playwrightAuthBypass) {
      this.resetAdminStatus();
      return;
    }

    const claimDecision = this.getAdminDecisionFromClaims(account);
    if (claimDecision !== null) {
      this.resetAdminStatus();
      return;
    }

    const accountKey = this.getAdminStatusAccountKey(account);

    void Promise.resolve().then(() => this.startBackendAdminStatusRefresh(accountKey));
  }

  private startBackendAdminStatusRefresh(accountKey: string): void {
    const account = this._account();

    if (!account || this.getAdminStatusAccountKey(account) !== accountKey) {
      return;
    }

    this.ensureBackendAdminStatus(account)
      .pipe(takeUntil(this.destroying$))
      .subscribe();
  }

  private ensureBackendAdminStatus(account: AccountInfo): Observable<boolean> {
    const accountKey = this.getAdminStatusAccountKey(account);

    if (this.adminStatusAccountKey === accountKey) {
      if (this.adminStatusRequest$) {
        return this.adminStatusRequest$;
      }

      const backendStatus = this._backendAdminStatus();
      if (backendStatus !== null) {
        return of(backendStatus);
      }
    }

    this.adminStatusAccountKey = accountKey;
    this._backendAdminStatus.set(null);
    this._adminStatusLoading.set(true);

    this.adminStatusRequest$ = this.injector.get(AdminApiService).getCurrentUserStatus().pipe(
      map((status) => status.isAdmin === true),
      catchError(() => of(false)),
      tap((isAdmin) => {
        if (this.adminStatusAccountKey === accountKey) {
          this._backendAdminStatus.set(isAdmin);
        }
      }),
      finalize(() => {
        if (this.adminStatusAccountKey === accountKey) {
          this._adminStatusLoading.set(false);
          this.adminStatusRequest$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.adminStatusRequest$;
  }

  private resetAdminStatus(): void {
    this.adminStatusAccountKey = null;
    this.adminStatusRequest$ = null;
    this._backendAdminStatus.set(null);
    this._adminStatusLoading.set(false);
  }

  private getAdminDecisionFromClaims(account: AccountInfo): AdminClaimDecision {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const rawGroups = claims?.['groups'];
    const groups = Array.isArray(rawGroups)
      ? rawGroups.filter((group): group is string => typeof group === 'string')
      : undefined;

    if (groups?.includes(environment.seoAdminsGroupId)) {
      return true;
    }

    if (!groups || this.hasGroupOverageMetadata(claims)) {
      return null;
    }

    return false;
  }

  private hasGroupOverageMetadata(claims: Record<string, unknown> | undefined): boolean {
    if (!claims) {
      return false;
    }

    const claimNames = claims['_claim_names'];
    const hasGroupClaimSource = this.isRecord(claimNames) && Boolean(claimNames['groups']);
    const hasGroupsMarker = claims['hasgroups'] === true || claims['hasgroups'] === 'true';

    return hasGroupClaimSource || hasGroupsMarker;
  }

  private getAdminStatusAccountKey(account: AccountInfo): string {
    return account.homeAccountId || account.localAccountId || account.username;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
